import { useState } from "react";
import api from "../lib/api";
import { APP_NAME, APP_TAGLINE, CJ_AVATAR_URL, TC_LOGO_URL } from "../lib/constants";
import { motion } from "framer-motion";
import { Mail, Sparkles, Loader2 } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSent(false);
    try {
      await api.post("/auth/request-magic-link", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tc-bg-soft)] p-4">
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 120, damping: 18 }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-7 gap-3">
          <motion.img
            src={CJ_AVATAR_URL}
            alt="CJ"
            data-testid="login-cj-avatar"
            initial={{ scale: 0.8, opacity: 0, rotate: -6 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 140, damping: 12, delay: 0.1 }}
            className="h-28 w-28 rounded-full object-cover shadow-xl ring-4 ring-white"
          />
          <div className="text-center">
            <div className="font-display text-3xl text-tc-ink leading-none" data-testid="login-app-name">{APP_NAME}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mt-1.5">{APP_TAGLINE}</div>
          </div>
        </div>

        <div className="tc-card p-7 sm:p-9">
          <div className="tc-eyebrow text-slate-400">It's CJ!</div>
          <h1 className="font-display text-3xl text-tc-ink mt-1 leading-tight">Every story. Every win. <span className="text-[#f68b1f]">Every dollar of impact.</span> All in one place.</h1>
          <p className="text-slate-500 mt-3 leading-relaxed text-sm">
            The full True Colors playbook lives behind this door. Stories, impact reports, kudos, pricing, facilitators, and resources. Pop in your <span className="font-bold text-slate-700">@truecolorsintl.com</span> email for a magic link. <span aria-hidden="true">🧡</span>
          </p>

          {!sent ? (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="tc-eyebrow text-slate-500 mb-1.5 block">Work email</span>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@truecolorsintl.com"
                    data-testid="login-email-input"
                    className="w-full h-14 pl-11 pr-4 rounded-2xl border-2 border-slate-200 focus:border-tc-orange focus:outline-none transition-colors text-base font-medium"
                  />
                </div>
              </label>
              {error && (
                <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>
              )}
              <button
                type="submit"
                disabled={loading || !email}
                data-testid="login-submit-button"
                className="w-full h-14 rounded-2xl text-white font-extrabold tracking-tight flex items-center justify-center gap-2 disabled:opacity-50 hover:-translate-y-0.5 transition-all shadow-md"
                style={{ background: "#f68b1f" }}
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : <><Sparkles size={18} /> Send magic link</>}
              </button>
            </form>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-sm text-green-900" data-testid="login-sent-message">
                <div className="font-bold mb-1">Magic link on the way 🎉</div>
                Check your inbox at <span className="font-mono">{email}</span>. Link expires in 15 minutes.
              </div>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="w-full h-11 rounded-xl border border-slate-200 font-bold text-slate-700 text-sm hover:bg-slate-100"
                data-testid="login-back-button"
              >
                Use a different email
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-5 text-[11px] text-slate-400 uppercase tracking-[0.2em] font-bold">
          Powered by True Colors
        </div>
      </motion.div>
    </div>
  );
}
